import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SourceDatabaseService from '../../services/SourceDatabaseService'; // Use the SourceDatabaseService for fetching source databases
import Spinner from '../Spinner'; // Optional: Add a loading spinner component
import Swal from 'sweetalert2'; // For confirmation dialogs

const SourceDatabaseList = () => {
    const [databases, setDatabases] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [databasesPerPage] = useState(5);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDatabases();
    }, []);

    const fetchDatabases = async () => {
        setLoading(true);
        setError(null); // Reset any previous errors
        try {
            const data = await SourceDatabaseService.getAll();
            setDatabases(data);
        } catch (err) {
            console.error('Error fetching source databases:', err);
            setError('Failed to fetch source databases. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'Do you really want to delete this database?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
        });

        if (result.isConfirmed) {
            try {
                await SourceDatabaseService.delete(id);
                Swal.fire('Deleted!', 'Source database deleted successfully.', 'success');
                fetchDatabases();
            } catch (error) {
                console.error('Error deleting database:', error);
                Swal.fire('Error!', 'An error occurred while deleting the database.', 'error');
            }
        }
    };

    const filteredDatabases = databases.filter((database) =>
        database.database_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const indexOfLastDatabase = currentPage * databasesPerPage;
    const indexOfFirstDatabase = indexOfLastDatabase - databasesPerPage;
    const currentDatabases = filteredDatabases.slice(indexOfFirstDatabase, indexOfLastDatabase);

    const totalPages = Math.ceil(filteredDatabases.length / databasesPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    if (loading) {
        return <Spinner />; // Optional: Display a spinner while loading
    }

    if (error) {
        return (
            <div className="alert alert-danger text-center" role="alert">
                {error}
            </div>
        );
    }

    return (
        <div className="content-wrapper">
            <div className="row">
                <div className="col-md-12">
                    <div className="card">
                        <div className="card-body">
                            <h4 className="card-title">Source Databases</h4>
                            <p className="card-description">View and manage all source databases</p>

                            {/* Add New Button */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <Link to="/source-databases/create" className="btn btn-primary btn-sm">
                                    <i className="ti-plus menu-icon"></i> Add New Database
                                </Link>
                                <input
                                    type="text"
                                    className="form-control form-control-sm w-50"
                                    placeholder="Search databases..."
                                    value={searchQuery}
                                    onChange={handleSearch}
                                />
                            </div>

                            {/* Source Databases Table */}
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Host</th>
                                        <th>Port</th>
                                        <th>Database Name</th>
                                        <th>Username</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentDatabases.length > 0 ? (
                                        currentDatabases.map((database) => (
                                            <tr key={database.id}>
                                                <td>{database.host}</td>
                                                <td>{database.port}</td>
                                                <td>{database.database_name}</td>
                                                <td>{database.username}</td>
                                                <td>
                                                    <Link
                                                        to={`/source-databases/edit/${database.id}`}
                                                        className="btn btn-sm btn-primary mr-2"
                                                    >
                                                        Edit
                                                    </Link>
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleDelete(database.id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center">
                                                No source databases found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <nav>
                                    <ul className="pagination justify-content-center mt-3">
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                            <li
                                                key={page}
                                                className={`page-item ${page === currentPage ? 'active' : ''}`}
                                            >
                                                <button onClick={() => paginate(page)} className="page-link">
                                                    {page}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </nav>
                            )}
                        </div>

                        <div className="card-footer text-muted">
                            Showing {currentDatabases.length} of {filteredDatabases.length} source databases
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SourceDatabaseList;
